import { useEffect, useRef, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { recordRecoveryRefreshAttempt, setForcedLogoutReason } from '@/lib/recoveryRefreshLoop'
import { dispatchForceLogout } from '@/lib/authEvents'

/**
 * Auth0 transaction-class failures: the OAuth handshake reached the callback
 * without its stored state/PKCE verifier (the iOS in-app-browser context-switch
 * bug). These are safe to auto-retry — restarting the redirect mints a fresh
 * transaction. `state_mismatch` / `missing_transaction` are the `.error` codes
 * auth0-spa-js throws with message "Invalid state"; `invalid_grant` covers a
 * stale/consumed authorization code ("Invalid authorization code").
 */
const RECOVERABLE_ERROR_CODES = new Set(['state_mismatch', 'missing_transaction', 'invalid_grant'])
const RECOVERABLE_MESSAGE_RE = /invalid state|invalid authorization code/i

type Auth0ErrorLike = Error & { error?: string; error_description?: string }

function isRecoverableAuthError(error: Error): boolean {
  const code = (error as Auth0ErrorLike).error
  if (typeof code === 'string' && code.length > 0) {
    // A machine code is present: trust it exclusively. Deliberate OAuth
    // decisions (access_denied / login_required / consent_required …) fall
    // through to non-recoverable on purpose.
    return RECOVERABLE_ERROR_CODES.has(code)
  }
  // Plain Error with no code: fall back to the human-readable message.
  return RECOVERABLE_MESSAGE_RE.test(error.message ?? '')
}

function restartLogin(loginWithRedirect: ReturnType<typeof useAuth0>['loginWithRedirect']): Promise<void> {
  // Drop the stale ?code & ?state so a reload/back-nav can't replay the dead
  // code, then start a fresh transaction on the current origin (mirrors
  // AuthController.redirectToLogin).
  window.history.replaceState({}, document.title, '/auth/callback')
  return loginWithRedirect({
    appState: { returnTo: '/' },
    authorizationParams: { redirect_uri: `${window.location.origin}/auth/callback` },
  })
}

export function AuthCallbackPage(): JSX.Element {
  const { isLoading, isAuthenticated, error, loginWithRedirect } = useAuth0()

  // Mirrors AuthController's redirectInFlightRef: the recovery branch fires at
  // most once per mount, immune to re-renders while loginWithRedirect navigates
  // away. Set before the async call, reset only on failure.
  const recoveryInFlightRef = useRef(false)
  const [unrecoverable, setUnrecoverable] = useState<Error | null>(null)

  // Success path — unchanged.
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      window.history.replaceState({}, document.title, '/')
      window.dispatchEvent(new Event('app:navigate'))
    }
  }, [isLoading, isAuthenticated])

  // Graceful recovery on a callback error.
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!error || isLoading) return
    if (recoveryInFlightRef.current) return

    if (!isRecoverableAuthError(error)) {
      setUnrecoverable(error)
      return
    }

    recoveryInFlightRef.current = true

    const { shouldForceLogout } = recordRecoveryRefreshAttempt()
    if (shouldForceLogout) {
      // Too many recovery attempts in the window (e.g. cookies are ALSO blocked
      // so the primary fix can't take effect). Hand off to AuthController to log
      // out cleanly and break the loop instead of bouncing through Auth0 again.
      setForcedLogoutReason('recovery-refresh-loop')
      dispatchForceLogout({ reason: 'recovery-refresh-loop' })
      return
    }

    void restartLogin(loginWithRedirect).catch((retryError: unknown) => {
      // The redirect itself failed (rare). Release the guard and surface the
      // error so the user gets a manual retry instead of a stuck spinner.
      recoveryInFlightRef.current = false
      setUnrecoverable(retryError instanceof Error ? retryError : error)
    })
  }, [error, isLoading, loginWithRedirect])

  if (unrecoverable) {
    const retry = (): void => {
      setUnrecoverable(null)
      recoveryInFlightRef.current = true
      void restartLogin(loginWithRedirect).catch(() => {
        recoveryInFlightRef.current = false
        setUnrecoverable(unrecoverable)
      })
    }
    return (
      <div className="flex min-h-screen items-center justify-center bg-navy text-white">
        <div className="w-full max-w-md text-center">
          <p className="text-lg font-semibold">Sign in didn’t complete</p>
          <p className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-100">
            {unrecoverable.message}
          </p>
          <button
            type="button"
            onClick={retry}
            className="mt-4 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-navy"
          >
            Try signing in again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-navy text-white">
      <div className="w-full max-w-md text-center">
        <p className="text-lg font-semibold">Completing sign in…</p>
        <p className="mt-2 text-sm text-white/70">
          You will be redirected shortly. If nothing happens, refresh the page.
        </p>
      </div>
    </div>
  )
}
